import {
  OrganizationInfo,
  SubOrganizationInfo,
  AccountInfo,
  AWSInfo,
  AzureInfo,
  GCPAccountInfo,
  AccountStatusEnum,
  OrganizationInfo_IncludeAccountIdListEntry,
  CloudTypeEnum,
  OktaInfo
} from '@src/endpoints/services/base.service'
import {
  map,
  sum,
  reduce,
  compact,
  flattenDeep,
  uniq,
  find,
  filter,
  forEach,
  isEmpty,
  has
} from 'lodash'
import type {
  OrgAccount,
  Folder,
  OrgStructure,
  CloudAccount,
  ScopeAccount,
  CloudAccountInfo
} from '../models'
import mapToDictionary from '@src/utils/dictionary'
import { getFieldsByType } from '@src/modules/accounts/configurations'

const awsFields = getFieldsByType(CloudTypeEnum.AWS)
const azureFields = getFieldsByType(CloudTypeEnum.AZURE)
const azureADFields = getFieldsByType(CloudTypeEnum.IDP_AZURE_AD)
const gcpFields = getFieldsByType(CloudTypeEnum.GCP)
const oktaFields = getFieldsByType(CloudTypeEnum.IDP_OKTA)

interface ParseScopeAccountsOptions {
  scopeAccounts: ScopeAccount[]
  includeAccountIds?: OrganizationInfo_IncludeAccountIdListEntry
  defaultValue?: boolean
}

export function parseScopeAccounts({
  scopeAccounts,
  includeAccountIds = {},
  defaultValue = true
}: ParseScopeAccountsOptions): OrganizationInfo_IncludeAccountIdListEntry {
  return mapToDictionary({
    array: scopeAccounts,
    key: 'id',
    iterator: x =>
      includeAccountIds[x.id] !== void 0
        ? includeAccountIds[x.id]
        : defaultValue
  })
}

export function isRoot(account: AccountInfo = {}) {
  const isAWS = account?.cloud_type === CloudTypeEnum.AWS
  return !isAWS && !account?.not_configurable
}

const isActive = (data: { status?: AccountStatusEnum }) =>
  data?.status === AccountStatusEnum.ACCOUNT_STATUS_SUCCESS

const collectionMessages = (
  ...args: { messages: { [key: string]: string } }[]
) => {
  let messages: { [key: string]: string } = {}
  forEach(args, x => {
    if (!isEmpty(x?.messages)) {
      messages = { ...messages, ...x.messages }
    }
  })
  return messages
}

function parseAccountOrganization(account: AccountInfo = {}): OrgAccount {
  return { ...account, isRoot: isRoot(account) }
}

function parseAccountsOrganization(data: AccountInfo[] = []): OrgAccount[] {
  return map(data, x => parseAccountOrganization(x))
}

function getScopeAccounts(accounts: OrgAccount[] = [], folders: Folder[] = []) {
  const sub = map(accounts || [], a => ({
    id: a.account_id,
    name: a.account_name
  }))
  return [
    ...sub,
    ...uniq(
      compact(
        flattenDeep(map(folders || [], f => f.scopeAccounts) || []) || []
      ) || []
    )
  ]
}

function parseSubOrganization(subOrg: SubOrganizationInfo = {}): Folder {
  const { sub_organization = [], account_info = [] } = subOrg
  const folders = parseSubOrganizations(sub_organization)
  const countByFolders = sum(map(folders, f => f?.count)) || 0
  const accounts = parseAccountsOrganization(account_info)
  const subAccounts = filter(accounts || [], a => !a?.isRoot)
  const countByAccounts = subAccounts?.length || 0
  const count = countByFolders + countByAccounts
  const alertsCountByAccount = getAlertsByOrg(accounts)
  const alertsCountByFolder = getAlertsByOrgFolder(folders)
  const countWarnings =
    (alertsCountByAccount?.warning || 0) +
      (alertsCountByFolder?.warning || 0) || 0
  const countErrors =
    (alertsCountByAccount?.error || 0) + (alertsCountByFolder?.error || 0) || 0
  const countActivities =
    (alertsCountByAccount?.active || 0) + (alertsCountByFolder?.active || 0) ||
    0

  const messages = collectionMessages(alertsCountByFolder, alertsCountByAccount)

  const scopeAccounts = getScopeAccounts(subAccounts, folders)

  return {
    original: { ...subOrg },
    scopeAccounts,
    folders,
    accounts: subAccounts,
    count,
    countWarnings,
    countErrors,
    countActivities,
    isActive: countActivities > 0,
    messages
  }
}

function parseSubOrganizations(data: SubOrganizationInfo[] = []): Folder[] {
  return map(data, x => parseSubOrganization(x))
}

export function parseOrganization(data: OrganizationInfo = {}): OrgStructure {
  const {
    aws_organization_info,
    azure_organization_info,
    gcp_organization_info,
    sub_organization = [],
    account_info = [],
    internal_error,
    cloud_type
  } = data

  const descriptions = []

  aws_organization_info &&
    descriptions.push(...parseAWSInfo(aws_organization_info))
  azure_organization_info &&
    descriptions.push(...parseAzureInfo(azure_organization_info, cloud_type))

  gcp_organization_info &&
    descriptions.push(...parseGCPInfo(gcp_organization_info))

  const accounts = parseAccountsOrganization(account_info)
  const subAccounts = filter(accounts || [], a => !a?.isRoot)
  const folders = parseSubOrganizations(sub_organization)
  const countByFolders = sum(map(folders, f => f?.count)) || 0
  const countByAccounts = subAccounts?.length || 0
  const count = countByFolders + countByAccounts
  const alertsCountByAccount = getAlertsByOrg(accounts)
  const alertsCountByFolder = getAlertsByOrgFolder(folders)
  const countWarnings =
    (alertsCountByAccount?.warning || 0) +
      (alertsCountByFolder?.warning || 0) || 0
  const countErrors =
    (alertsCountByAccount?.error || 0) + (alertsCountByFolder?.error || 0) || 0

  const countActivities =
    (alertsCountByAccount?.active || 0) + (alertsCountByFolder?.active || 0) ||
    0

  const scopeAccounts = getScopeAccounts(subAccounts, folders)
  const parentAccount = find(accounts || [], a => a?.isRoot)

  const orgErrors = internal_error
    ? { messages: { [data?.organization_uid]: internal_error } }
    : { messages: {} }

  const messages = collectionMessages(
    orgErrors,
    alertsCountByFolder,
    alertsCountByAccount
  )
  return {
    parentAccount,
    original: { ...data },
    scopeAccounts,
    folders,
    accounts: subAccounts,
    descriptions,
    count,
    countWarnings,
    countErrors,
    isActive: countActivities > 0,
    messages
  }
}

export function parseOrganizationBeta(
  data: OrganizationInfo = {}
): OrgStructure {
  const {
    aws_organization_info,
    azure_organization_info,
    gcp_organization_info,
    sub_organization = [],
    account_info = [],
    internal_error,
    cloud_type
  } = data

  const descriptions = []

  aws_organization_info &&
    descriptions.push(...parseAWSInfo(aws_organization_info))
  azure_organization_info &&
    descriptions.push(...parseAzureInfo(azure_organization_info, cloud_type))

  gcp_organization_info &&
    descriptions.push(...parseGCPInfo(gcp_organization_info))

  const accounts = parseAccountsOrganization(account_info)
  const subAccounts = filter(accounts || [], a => !a?.isRoot)
  const folders = parseSubOrganizations(sub_organization)
  const countByFolders = sum(map(folders, f => f?.count)) || 0
  const countByAccounts = subAccounts?.length || 0
  const count = countByFolders + countByAccounts
  const alertsCountByAccount = getAlertsByOrg(accounts)
  const alertsCountByFolder = getAlertsByOrgFolder(folders)
  const countWarnings =
    (alertsCountByAccount?.warning || 0) +
      (alertsCountByFolder?.warning || 0) || 0
  const countErrors =
    (alertsCountByAccount?.error || 0) + (alertsCountByFolder?.error || 0) || 0

  const countActivities =
    (alertsCountByAccount?.active || 0) + (alertsCountByFolder?.active || 0) ||
    0

  const scopeAccounts = getScopeAccounts(subAccounts, folders)
  const parentAccount = find(accounts || [], a => a?.isRoot)

  const orgErrors = internal_error
    ? { messages: { organization_id: internal_error } }
    : { messages: {} }

  const messages = collectionMessages(
    orgErrors,
    alertsCountByFolder,
    alertsCountByAccount
  )
  return {
    parentAccount,
    original: { ...data },
    scopeAccounts,
    folders,
    accounts: subAccounts,
    descriptions,
    count,
    countWarnings,
    countErrors,
    isActive: countActivities > 0,
    messages
  }
}

export function parseCloudAccount(data: AccountInfo = {}): CloudAccount {
  const {
    status_reason,
    aws_info,
    azureInfo,
    gcp_info,
    okta_info,
    cloud_type
  } = data

  const descriptions = []

  aws_info && descriptions.push(...parseAWSInfo(aws_info))
  azureInfo && descriptions.push(...parseAzureInfo(azureInfo, cloud_type))
  okta_info && descriptions.push(...parseOktaInfo(okta_info))
  gcp_info && descriptions.push(...parseGCPInfo(gcp_info))

  const messages = status_reason ? { [data?.uid]: status_reason } : {}
  return {
    original: { ...data },
    descriptions,
    isWarning: data?.status === AccountStatusEnum.ACCOUNT_STATUS_WARNING,
    isError: data?.status === AccountStatusEnum.ACCOUNT_STATUS_ERROR,
    isActive: isActive(data),
    messages
  }
}

export function parseCloudAccountBeta(data: AccountInfo = {}): CloudAccount {
  const {
    status_reason,
    aws_info,
    azureInfo,
    gcp_info,
    okta_info,
    cloud_type
  } = data

  const descriptions = []

  aws_info && descriptions.push(...parseAWSInfo(aws_info))
  azureInfo && descriptions.push(...parseAzureInfo(azureInfo, cloud_type))
  okta_info && descriptions.push(...parseOktaInfo(okta_info))
  gcp_info && descriptions.push(...parseGCPInfo(gcp_info))

  const messages = status_reason ? { account_id: status_reason } : {}
  return {
    original: data,
    descriptions,
    isWarning: status_reason === AccountStatusEnum.ACCOUNT_STATUS_WARNING,
    isError: status_reason === AccountStatusEnum.ACCOUNT_STATUS_ERROR,
    isActive: status_reason === AccountStatusEnum.ACCOUNT_STATUS_SUCCESS,
    messages
  }
}

export function parseCloudAccounts(data: AccountInfo[] = []) {
  return map(data, x => parseCloudAccount(x))
}

export function parseCloudAccountsBeta(data: CloudAccountInfo[] = []) {
  return reduce(
    data,
    (acc, x) => {
      const cloudType =
        x?.account?.cloud_type ||
        x?.cloud_type ||
        CloudTypeEnum.CLOUD_TYPE_UNKNOWN
      if (has(acc, cloudType)) {
        acc[cloudType].push(parseCloudAccountBeta(x))
        return acc
      }
      acc[cloudType] = [parseCloudAccountBeta(x)]
      return acc
    },
    {}
  )
}

export function parseOrganizations(data: OrganizationInfo[] = []) {
  return map(data, x => parseOrganization(x))
}

export function parseOrganizationsBeta(data: OrganizationInfo[] = []) {
  return reduce(
    data,
    (acc, x) => {
      const cloudType = x?.cloud_type || CloudTypeEnum.CLOUD_TYPE_UNKNOWN
      if (has(acc, cloudType)) {
        acc[cloudType].push(parseOrganizationBeta(x))
        return acc
      }
      acc[cloudType] = [parseOrganizationBeta(x)]
      return acc
    },
    {}
  )
}

export function parseAWSInfo(data: AWSInfo = {}) {
  const { role_arn, cloud_trail_arn } = data
  const list = []
  role_arn &&
    list.push({
      label: `${awsFields?.role_arn?.label}`,
      value: role_arn
    })
  cloud_trail_arn &&
    list.push({
      label: `${awsFields?.cloud_trail_arn?.label}`,
      value: cloud_trail_arn
    })
  return list
}

export function parseAzureInfo(data: AzureInfo = {}, cloudType: CloudTypeEnum) {
  if (cloudType === CloudTypeEnum.IDP_AZURE_AD) {
    return parseAzureADInfo(data)
  }
  const { app_client_id, tenant_id } = data
  const list = []
  app_client_id &&
    list.push({
      label: `${azureFields?.app_client_id?.label}`,
      value: app_client_id
    })
  tenant_id &&
    list.push({
      label: `${azureFields?.tenant_id?.label}`,
      value: tenant_id
    })
  return list
}

function parseAzureADInfo(data: AzureInfo = {}) {
  const { app_client_id, tenant_id } = data
  const list = []
  app_client_id &&
    list.push({
      label: `${azureADFields?.app_client_id?.label}`,
      value: app_client_id
    })
  tenant_id &&
    list.push({
      label: `${azureADFields?.tenant_id?.label}`,
      value: tenant_id
    })

  return list
}

function parseOktaInfo(data: OktaInfo = {}) {
  const { application_id, org_url } = data
  const list = []
  org_url &&
    list.push({
      label: `${oktaFields?.org_url?.label}`,
      value: org_url
    })
  application_id &&
    list.push({
      label: `${oktaFields?.application_id?.label}`,
      value: application_id
    })

  return list
}

function parseGCPInfo(data: GCPAccountInfo = {}) {
  const { domain, organization_id } = data
  const list = []
  organization_id &&
    list.push({
      label: `${gcpFields?.organization_id?.label}`,
      value: organization_id
    })
  domain &&
    list.push({
      label: `${gcpFields?.domain?.label}`,
      value: domain
    })

  return list
}

export function getAlerts(data: OrgAccount) {
  const { status_reason } = data || {}
  const messages = status_reason ? { [data?.uid]: data?.status_reason } : {}
  return {
    warning: data.status === AccountStatusEnum.ACCOUNT_STATUS_WARNING ? 1 : 0,
    error: data.status === AccountStatusEnum.ACCOUNT_STATUS_ERROR ? 1 : 0,
    active: isActive(data) ? 1 : 0,
    messages
  }
}

function getAlertsByOrg(data: OrgAccount[]) {
  const alerts = map(data, x => getAlerts(x))
  const messages = collectionMessages(...map(alerts, x => x))

  return {
    warning: reduce(alerts, (s, k) => s + k.warning || 0, 0),
    error: reduce(alerts, (s, k) => s + k.error || 0, 0),
    active: reduce(alerts, (s, k) => s + k.active || 0, 0),
    messages
  }
}

function getAlertsByOrgFolder(data: Folder[]) {
  const messages = collectionMessages(...map(data, x => x))
  return {
    warning: reduce(data, (s, k) => s + k.countWarnings || 0, 0),
    error: reduce(data, (s, k) => s + k.countErrors || 0, 0),
    active: reduce(data, (s, k) => s + k.countActivities || 0, 0),
    messages
  }
}
